import {useCallback, useEffect, useRef, useState} from "react";
import {Report} from "../../../data/models/Report";
import {XyiconFeature} from "../../../generated/api/reports";
import {useAppStore} from "../../../StateManager";
import type {IViewTab} from "../topbar/ViewTabsCommon";
import {
	EventNameForViewsChangeInLocalStorage,
	getActiveWorkspaceViewTabFromLocalStorage,
	getLocalStorageKeyForWorkspaceViews,
	getWorkspaceViewTabsFromLocalStorage,
	runAndHandleReport,
} from "../topbar/ViewTabsCommon";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {LoaderV5} from "../loader/LoaderV5";
import {EmptyListViewV5} from "../details/EmptyListViewV5";
import {CreateViewPopup} from "../abstract/view/create/CreateViewPopup";
import {getKeyForLocalStorageWorkspaceViewSections, type WorkspaceViewType} from "../navigation/views/WorkspaceViewCommon";
import {Permission} from "../../../generated/api/base/models/Permission";
import {ReportPanelV5} from "./report/create/ReportPanelV5";
import {RunReportPageV5} from "./report/create/wizard/run/RunReportPageV5";

export const ReportResultViewV5 = () => {
	const appState = useAppStore((state) => state.appState);
	const navBarState = useAppStore((state) => state.navBarState);
	const isPortfolioSwitchRequestInProgress = useAppStore((state) => state.isPortfolioSwitchRequestInProgress);
	const [runningReport, setRunningReport] = useState<Report>(null);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [lastRunTime, setLastRunTime] = useState<number>(null);
	const [isNoActiveView, setIsNoActiveView] = useState<boolean>(true);
	const [activeFilePath, setActiveFilePath] = useState<string>("");
	const [createViewPopupType, setCreateViewPopupType] = useState<WorkspaceViewType | null>(null);
	const editReportRef = useRef<Report | null>(null);

	const STALENESS_THRESHOLD_SECONDS = 5;

	const onRunReport = async (report: Report, justUpdate?: boolean) => {
		const {user, organizationIdByAuthData} = appState;

		setRunningReport(report);
		setIsLoading(true);

		const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(user?.id, organizationIdByAuthData, report.appState.actions);
		const itemIndexInListMaybe = viewTabsInLocalStorage.findIndex((v) => v.viewId === report.id);
		const currentTab = viewTabsInLocalStorage[itemIndexInListMaybe];

		if (currentTab) {
			const timestamp = Date.now();
			currentTab.lastUsed = timestamp;

			if (justUpdate) {
				await runningReport.update();
				setIsLoading(false);
			} else {
				currentTab.lastRunTime = timestamp;
				setLastRunTime(currentTab.lastRunTime);
				await runAndHandleReport(runningReport, false);
				// setLoading(false) is not needed here, because this function will call the handleViewsChange function with the setLoading(false) within.
			}
		}
	};

	const onEditClick = (report: Report) => {
		report.startEditing();
		editReportRef.current = report;
		setIsEditing(true);
	};

	const onCloseEditingReports = () => {
		setIsEditing(false);
	};

	const onDuplicateClick = (reports: Report[]) => {
		const duplicate = reports[0].duplicate();

		editReportRef.current = duplicate as Report;
		appState.user?.updateViewFolderStructures();
		setIsEditing(true);

		const keyForLocalStorage = getKeyForLocalStorageWorkspaceViewSections(appState.user.id);
		let openViewSpaceTypes: WorkspaceViewType[] = JSON.parse(localStorage.getItem(keyForLocalStorage)) || null;

		if (!openViewSpaceTypes) {
			openViewSpaceTypes = ["Favorites", "Global", "My Views", "Shared With Me"];
			localStorage.setItem(keyForLocalStorage, JSON.stringify(openViewSpaceTypes));
		}

		const newOpenViewSpaceTypes = new Set([...openViewSpaceTypes]);

		if (duplicate.isGlobal) {
			if (!newOpenViewSpaceTypes.has("My Views")) {
				newOpenViewSpaceTypes.add("My Views");
			}
			localStorage.setItem(keyForLocalStorage, JSON.stringify([...newOpenViewSpaceTypes]));
		}

		return duplicate;
	};

	const onCreateReportClick = () => {
		editReportRef.current = new Report(appState);
		editReportRef.current.startEditing();
		setIsEditing(true);
	};

	const onCreateViewClick = () => {
		if (!createViewPopupType) {
			setCreateViewPopupType("My Views");
		}
	};

	const closeCreateViewPopup = () => {
		if (createViewPopupType) {
			setCreateViewPopupType(null);
		}
	};

	const canDeleteReport = (reports: Report[]) => {
		if (reports.length < 1) {
			return false;
		}

		for (const report of reports) {
			// cannot delete custom designed reports
			if (!report.isUserDefined) {
				return false;
			}

			// check permission
			const permission = Report.getPermission(report, appState);

			if (permission < Permission.Update) {
				return false;
			}
		}

		return true;
	};

	const handleViewsChange = useCallback(
		(e?: CustomEvent) => {
			const isPortfolioSwitched = !!e?.detail?.isPortfolioSwitched;
			const relatedTabViewId = e?.detail?.tabViewId ?? "";
			const noUpdate = !!e?.detail?.noUpdate;

			const {user, organizationIdByAuthData, actions, app} = appState;
			const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(user?.id, organizationIdByAuthData);
			const updatedValue = localStorage.getItem(localStorageKeyForWorkspaceViews);
			const viewIdsInLocalStorage: IViewTab[] = updatedValue ? JSON.parse(updatedValue) : [];

			setIsNoActiveView(viewIdsInLocalStorage.length === 0);

			const active = viewIdsInLocalStorage.find(
				(view) => view.viewId === getActiveWorkspaceViewTabFromLocalStorage(user?.id, organizationIdByAuthData)?.viewId,
			);
			const selectedView = active?.viewId ? actions.getReportViewById(active.viewId) : null;

			const loggedInTime = new Date(appState.app.transport.services.auth.authData.issuedAt).getTime();
			const isOutDated = active?.lastRunTime < loggedInTime;

			if (selectedView && (relatedTabViewId === "" || selectedView.id === relatedTabViewId) && !noUpdate) {
				const fileFormat = StringUtils.lowerCase(selectedView.deliveryFormat ?? "csv");
				const filePath = app.transport.getReportFilePath(active.requestId, fileFormat);
				const filePathForActiveTabEqualToRunningReportFilePath = filePath === activeFilePath;

				if (!filePathForActiveTabEqualToRunningReportFilePath) {
					selectedView.clearReport();
				}

				setRunningReport(selectedView);
				setLastRunTime(active.lastRunTime);

				const reportGeneratingIsInProgress = appState.reportsUnderGenerating.includes(active?.viewId);

				if (!reportGeneratingIsInProgress && active.requestId !== "" && !isPortfolioSwitched && !isOutDated) {
					if (!filePathForActiveTabEqualToRunningReportFilePath) {
						selectedView.completeReportViewRequest(filePath);
						setActiveFilePath(filePath);
						setIsLoading(false);
					}
				} else if ((active.requestId === "" && !reportGeneratingIsInProgress) || isOutDated) {
					runAndHandleReport(selectedView, isPortfolioSwitched);
				}
			}
		},
		[appState, activeFilePath],
	);

	useEffect(() => {
		window.addEventListener(EventNameForViewsChangeInLocalStorage, handleViewsChange);

		return () => {
			window.removeEventListener(EventNameForViewsChangeInLocalStorage, handleViewsChange);
		};
	}, [handleViewsChange]);

	useEffect(() => {
		handleViewsChange();
	}, [handleViewsChange]);

	useEffect(() => {
		const runReportIfOutdated = () => {
			const now = Date.now();
			const loggedInTime = new Date(appState.app.transport.services.auth.authData.issuedAt).getTime();

			if ((now - loggedInTime) / 1000 > STALENESS_THRESHOLD_SECONDS) {
				const {user, organizationIdByAuthData, actions} = appState;
				const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(user?.id, organizationIdByAuthData);
				const updatedValue = localStorage.getItem(localStorageKeyForWorkspaceViews);
				const viewIdsInLocalStorage: IViewTab[] = updatedValue ? JSON.parse(updatedValue) : [];
				const active = viewIdsInLocalStorage.find(
					(view) => view.viewId === getActiveWorkspaceViewTabFromLocalStorage(user?.id, organizationIdByAuthData)?.viewId,
				);
				const selectedView = active?.viewId ? actions.getReportViewById(active.viewId) : null;

				if (selectedView) {
					selectedView.clearReport();
				}
			}
		};

		runReportIfOutdated();
	}, [appState]);

	return (
		<>
			{isLoading || isPortfolioSwitchRequestInProgress ? (
				<LoaderV5 label="One moment please, we are running your report" />
			) : isNoActiveView ? (
				<EmptyListViewV5
					onAddClick={onCreateViewClick}
					feature={XyiconFeature.View}
					runReport={onRunReport}
				/>
			) : runningReport ? (
				<RunReportPageV5
					report={runningReport}
					onEditClick={onEditClick}
					onRunReport={onRunReport}
					lastRunTime={lastRunTime}
					onDuplicateClick={onDuplicateClick}
					canDeleteReports={canDeleteReport}
					style={{left: navBarState === "docked" ? 288 : ""}}
				/>
			) : (
				"No report found"
			)}
			{createViewPopupType && (
				<CreateViewPopup
					onClose={closeCreateViewPopup}
					type={createViewPopupType}
					viewFolderIdToSaveNewViewInto={""}
					onCreateReportView={onCreateReportClick}
					defaultTemplateFeature={XyiconFeature.Report}
				/>
			)}
			{isEditing && editReportRef.current && (
				<ReportPanelV5
					isReportView={true}
					report={editReportRef.current}
					onRunReport={() => onRunReport(runningReport, true)}
					onClose={(saveChanges: boolean) => {
						onCloseEditingReports();

						if (!saveChanges) {
							editReportRef.current?.cancelEditing();
						}
					}}
				/>
			)}
		</>
	);
};
